/**
 * NewChannelModal Component
 *
 * Modal to create a new project channel from /mensajes — see #444, the
 * "Proyectos" tab in /mensajes had no way to create a channel of its own.
 * It calls chatService.createConversation({ type: 'channel' }), the same call
 * the "Canal" tab of the old NewConversationModal used to make (that modal and
 * the /admin/mensajes surface it belonged to were removed in #448).
 *
 * A project is always required: the DB constraint
 * `chat_channel_requires_project` rejects channel rows with a null
 * project_id (#443), so this modal never offers a "Sin proyecto" option.
 *
 * @module components/messages/NewChannelModal
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../services/supabase';
import { createConversation } from '../../services/chatService';
import { toast } from '../../utils/toast';

const fetchProjects = async () => {
  const { data, error } = await supabase
    .from('projects')
    .select('id, title, project_number')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

const NewChannelModal = ({ show, onHide, onConversationOpen }) => {
  const queryClient = useQueryClient();
  const [channelName, setChannelName] = useState('');
  const [projectId, setProjectId] = useState('');
  const [submitError, setSubmitError] = useState(null);

  const { data: projects = [], isLoading, error } = useQuery({
    queryKey: ['chat-channel-projects'],
    queryFn: fetchProjects,
    enabled: show,
    staleTime: 1000 * 60,
  });

  const channelMutation = useMutation({
    mutationFn: async ({ name, project_id }) => {
      const { data, error: createError } = await createConversation({
        type: 'channel',
        name,
        project_id,
        participantIds: [],
      });
      if (createError) throw createError;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast.success(`Canal "${channelName}" creado`);
      if (onConversationOpen && data?.id) onConversationOpen(data.id);
      handleClose();
    },
    onError: (err) => {
      setSubmitError(err?.message || 'No se pudo crear el canal');
      toast.error(err?.message || 'No se pudo crear el canal');
    },
  });

  const handleClose = () => {
    setChannelName('');
    setProjectId('');
    setSubmitError(null);
    onHide();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitError(null);

    const trimmed = channelName.trim();
    if (!trimmed) {
      setSubmitError('El nombre del canal es requerido.');
      return;
    }
    if (!projectId) {
      setSubmitError('Selecciona un proyecto para el canal.');
      return;
    }

    channelMutation.mutate({ name: trimmed, project_id: projectId });
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-hash me-2"></i>
          Nuevo canal de proyecto
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              No se pudieron cargar los proyectos.
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Proyecto</Form.Label>
            <Form.Select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              disabled={isLoading}
              required
            >
              <option value="" disabled>Selecciona un proyecto...</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.project_number ? `${p.project_number} - ` : ''}{p.title}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Los canales estan siempre vinculados a un proyecto.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>Nombre del canal</Form.Label>
            <Form.Control
              type="text"
              placeholder="ej. general, soporte-tecnico"
              value={channelName}
              onChange={(e) => setChannelName(e.target.value)}
              maxLength={100}
              required
            />
          </Form.Group>

          {submitError && (
            <Alert variant="danger" className="mt-3 mb-0">
              {submitError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={channelMutation.isLoading || !projectId}
          >
            {channelMutation.isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creando...
              </>
            ) : (
              <>
                <i className="bi bi-plus-lg me-2"></i>
                Crear canal
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

NewChannelModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onConversationOpen: PropTypes.func,
};

export default NewChannelModal;
